import { useEffect, useMemo, useRef, useState } from 'react';
import {
  VsTransferEvent,
  useGetActiveHeroConversations,
  useGetAssociateOutgoingOpenTransferRequests,
  useQueriesCache,
  useVsWebsocket,
} from '@vs-online-only-app/state';
import { CollapsedTransferCards } from './outgoing-transfer/cards/CollapsedTransferCards';
import { ExpandedTransferCards } from './outgoing-transfer/cards/ExpandedTransferCards';
import { CancelledTransferBanner } from './outgoing-transfer/banners/CancelledTransferBanner';

export const OutgoingTransfers = () => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const { data: openRequests, refetch: refetchOpenRequests } =
    useGetAssociateOutgoingOpenTransferRequests();
  const [closedTransferRequests, setClosedTransferRequests] = useState<
    VsTransferEvent[]
  >([]);
  const [cancelledTransferRequests, setCancelledTransferRequests] = useState<
    VsTransferEvent[]
  >([]);
  const getActiveConversationsQuery = useGetActiveHeroConversations();
  const { outgoingTransfersUpdateEvents } = useVsWebsocket();
  const previousRequestsRef = useRef<VsTransferEvent[]>();
  const previousRequests = previousRequestsRef.current;
  const { invalidateHeroConversation } = useQueriesCache();

  useEffect(() => {
    // if we have a new transfer update event, one of the pending requests has reached an outcome (thus is not open any more)
    // refetch the most up-to-date list of open requests
    if (outgoingTransfersUpdateEvents.length) {
      const acceptedRequestEvent = outgoingTransfersUpdateEvents?.find(
        (updateEvent) => updateEvent.accepted
      );
      if (acceptedRequestEvent) {
        // we invalidate the cached hero conversation because if the request has been accepted,
        // the conversation details will be out of date
        invalidateHeroConversation(acceptedRequestEvent.conversation);
        getActiveConversationsQuery.refetch();
      }
      refetchOpenRequests();
    }
  }, [outgoingTransfersUpdateEvents]);

  const transferRequests = useMemo(() => {
    const allRequests: Record<string, VsTransferEvent> = {};
    //merge open and previous transfer requests and deduplicate
    [...(openRequests || []), ...(previousRequests || [])]?.forEach(
      (request) => {
        if (!Object.keys(allRequests).includes(request.id)) {
          allRequests[request.id] = request;
        }
      }
    );

    return (
      Object.values(allRequests)
        //filter out closed and cancelled requests
        .filter(
          (request) =>
            !closedTransferRequests.find(
              (closedRequest) => closedRequest.id === request.id
            ) &&
            !cancelledTransferRequests.find(
              (cancelledRequest) => cancelledRequest.id === request.id
            )
        )
        //append updates to transfer
        .map((request) => {
          const lastEvent = [...outgoingTransfersUpdateEvents]
            .reverse()
            .find((updateEvent) => updateEvent.id === request.id);

          return {
            ...request,
            ...lastEvent,
          };
        })
        //sort requests by latest desc
        .sort((requestA, requestB) => {
          return (
            new Date(requestB.creation).getTime() -
            new Date(requestA.creation).getTime()
          );
        })
    );
  }, [
    openRequests,
    previousRequests,
    outgoingTransfersUpdateEvents,
    closedTransferRequests,
    cancelledTransferRequests,
  ]);

  useEffect(() => {
    //keep previously open requests until associate closes outcome card
    previousRequestsRef.current = transferRequests;
  }, [transferRequests]);

  const CardsComponent = isExpanded
    ? ExpandedTransferCards
    : CollapsedTransferCards;

  return (
    <>
      {transferRequests.length ? (
        <CardsComponent
          onCancel={(request) => {
            setCancelledTransferRequests([
              ...cancelledTransferRequests,
              request,
            ]);
          }}
          onClose={(request) =>
            setClosedTransferRequests([...closedTransferRequests, request])
          }
          onClick={() => setIsExpanded(!isExpanded)}
          transferRequests={transferRequests}
        />
      ) : null}
      {cancelledTransferRequests.length
        ? cancelledTransferRequests.map((transferRequest) => (
            <CancelledTransferBanner
              transferRequest={transferRequest}
              key={transferRequest.id}
            />
          ))
        : null}
    </>
  );
};
